import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { Command } from 'cmdk'
import {
  BookOpen,
  BookOpenText,
  Boxes,
  CircleHelp,
  FileCode2,
  History,
  Layers,
  Monitor,
  Moon,
  Search,
  Sun,
  UserCog,
  Zap,
} from 'lucide-react'
import { Dialog as D } from 'radix-ui'
import { useState, type ReactNode } from 'react'
import { allApplicationsQuery } from '@/api/queries/applications'
import { paletteEventTypesQuery, paletteTemplatesQuery } from '@/api/queries/search'
import { DEV_ROLES, useAuth } from '@/auth/context'
import { usePermissions } from '@/auth/usePermissions'
import { Kbd } from '@/components/ui/kbd'
import { looksLikeKey } from '@/features/timelines/model'
import { useHelpIndex } from '@/help/hooks'
import { sectionById } from '@/help/sections'
import { substringFilter } from '@/lib/search'
import { useTheme } from '@/lib/theme'
import { NAV_ITEMS } from './nav'
import { useAppScope } from './useAppScope'

interface CommandPaletteProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Open the help drawer for the current page. */
  onOpenHelp: () => void
}

const groupClass =
  'px-1 pb-1 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-ink-3'

/**
 * Cmd/Ctrl+K: go to a page, jump to an application, template, or event type by
 * name, or find the timeline for a typed key value.
 */
export function CommandPalette({ open, onOpenChange: setOpen, onOpenHelp }: CommandPaletteProps) {
  const navigate = useNavigate()
  const { can } = usePermissions()
  const auth = useAuth()
  const { setPreference } = useTheme()
  const { appId, application, setAppId } = useAppScope()
  const apps = useQuery({ ...allApplicationsQuery, enabled: open })
  const templates = useQuery({ ...paletteTemplatesQuery(appId), enabled: open })
  const eventTypes = useQuery({ ...paletteEventTypesQuery(appId), enabled: open })
  const help = useHelpIndex(open)
  const [search, setSearch] = useState('')
  const onOpenChange = (next: boolean) => {
    if (!next) setSearch('')
    setOpen(next)
  }
  const appName = (id: string | null | undefined) =>
    id ? (apps.data?.find((a) => a.id === id)?.slug ?? '') : 'shared'

  const run = (fn: () => void) => {
    onOpenChange(false)
    fn()
  }

  return (
    <D.Root open={open} onOpenChange={onOpenChange}>
      <D.Portal>
        <D.Overlay className="fixed inset-0 z-40 animate-fade-in bg-overlay" />
        <D.Content
          aria-label="Command palette"
          className="fixed top-[14vh] left-1/2 z-50 w-[calc(100vw-2rem)] max-w-xl -translate-x-1/2 animate-in overflow-hidden rounded-xl border border-line bg-surface shadow-pop outline-none"
        >
          <D.Title className="sr-only">Command palette</D.Title>
          <D.Description className="sr-only">
            Search pages, applications, templates, event types, and help articles.
          </D.Description>
          <Command loop filter={substringFilter} className="flex max-h-[min(520px,70vh)] flex-col">
            <div className="flex items-center gap-2 border-b border-line px-3.5">
              <Search className="size-4 shrink-0 text-ink-3" />
              <Command.Input
                autoFocus
                value={search}
                onValueChange={setSearch}
                placeholder={
                  application
                    ? `Search in ${application.name}…`
                    : 'Search pages, applications, templates, event types, help…'
                }
                className="h-12 w-full bg-transparent text-base outline-none placeholder:text-ink-3"
              />
              <Kbd>Esc</Kbd>
            </div>
            <Command.List className="min-h-0 flex-1 overflow-y-auto py-1">
              {!looksLikeKey(search) && (
                <Command.Empty className="px-4 py-8 text-center text-sm text-ink-2">
                  No results
                </Command.Empty>
              )}

              {/* Typed text that looks like an order number or name=value. */}
              {looksLikeKey(search) && (
                <Command.Group heading="Timeline" forceMount className={groupClass}>
                  <PaletteItem
                    value={`timeline ${search.trim()}`}
                    forceMount
                    icon={<History />}
                    label={`Find timeline for ${search.trim()}`}
                    onSelect={() => {
                      const key = search.trim()
                      run(() => void navigate({ to: '/timelines', search: (p) => ({ ...p, key }) }))
                    }}
                  />
                </Command.Group>
              )}

              <Command.Group heading="Go to" className={groupClass}>
                {NAV_ITEMS.filter((i) => !i.permission || can(i.permission)).map((item) => (
                  <PaletteItem
                    key={item.to}
                    value={`go ${item.label}`}
                    icon={<item.icon />}
                    label={item.label}
                    shortcut={item.shortcut}
                    onSelect={() => run(() => void navigate({ to: item.to }))}
                  />
                ))}
                <PaletteItem
                  value="go help center"
                  keywords={['Help center', 'documentation', 'docs', 'guide']}
                  icon={<BookOpen />}
                  label="Help center"
                  onSelect={() => run(() => void navigate({ to: '/help' }))}
                />
              </Command.Group>

              {apps.data && apps.data.length > 0 && (
                <Command.Group heading="Applications" className={groupClass}>
                  {apps.data.map((a) => (
                    <PaletteItem
                      key={a.id}
                      value={`app ${a.id}`}
                      keywords={[a.name, a.slug]}
                      icon={<Boxes />}
                      label={a.name}
                      hint={a.slug}
                      onSelect={() =>
                        run(
                          () =>
                            void navigate({ to: '/applications/$appId', params: { appId: a.id } }),
                        )
                      }
                    />
                  ))}
                </Command.Group>
              )}

              {templates.data && templates.data.length > 0 && (
                <Command.Group heading="Templates" className={groupClass}>
                  {templates.data.map((t) => (
                    <PaletteItem
                      key={t.id}
                      value={`tpl ${t.id}`}
                      keywords={[t.name, t.key]}
                      icon={<FileCode2 />}
                      label={t.name}
                      hint={`${appName(t.application_id)} / ${t.key}`}
                      onSelect={() =>
                        run(
                          () =>
                            void navigate({
                              to: '/templates/$templateId',
                              params: { templateId: t.id },
                            }),
                        )
                      }
                    />
                  ))}
                </Command.Group>
              )}

              {eventTypes.data && eventTypes.data.length > 0 && (
                <Command.Group heading="Event types" className={groupClass}>
                  {eventTypes.data.map((e) => (
                    <PaletteItem
                      key={e.id}
                      value={`evt ${e.id}`}
                      keywords={[e.name, e.key]}
                      icon={<Zap />}
                      label={e.name}
                      hint={`${appName(e.application_id)} / ${e.key}`}
                      onSelect={() =>
                        run(
                          () =>
                            void navigate({
                              to: '/event-types/$eventTypeId',
                              params: { eventTypeId: e.id },
                            }),
                        )
                      }
                    />
                  ))}
                </Command.Group>
              )}

              {/* Articles appear once you type, so the default list stays short. */}
              {search.trim() !== '' && help.data && help.data.length > 0 && (
                <Command.Group heading="Help" className={groupClass}>
                  {help.data.map((a) => (
                    <PaletteItem
                      key={a.slug}
                      value={`help ${a.slug}`}
                      keywords={[a.title, a.summary, ...a.headings.map((h) => h.text)]}
                      icon={<BookOpenText />}
                      label={a.title}
                      hint={sectionById(a.section).title}
                      onSelect={() =>
                        run(() => void navigate({ to: '/help/$slug', params: { slug: a.slug } }))
                      }
                    />
                  ))}
                </Command.Group>
              )}

              <Command.Group heading="Actions" className={groupClass}>
                <PaletteItem
                  value="help for this page"
                  keywords={['Help for this page', 'what is this page', 'docs']}
                  icon={<CircleHelp />}
                  label="Help for this page"
                  shortcut="?"
                  onSelect={() => run(onOpenHelp)}
                />
                {appId && (
                  <PaletteItem
                    value="scope all applications"
                    icon={<Layers />}
                    label="Show all applications"
                    onSelect={() => run(() => setAppId(undefined))}
                  />
                )}
                {apps.data?.map((a) =>
                  a.id === appId ? null : (
                    <PaletteItem
                      key={`scope-${a.id}`}
                      value={`scope ${a.id}`}
                      keywords={[`scope ${a.name}`, `filter ${a.slug}`]}
                      icon={<Layers />}
                      label={`Scope to ${a.name}`}
                      onSelect={() => run(() => setAppId(a.id))}
                    />
                  ),
                )}
                <PaletteItem
                  value="theme light"
                  icon={<Sun />}
                  label="Use light theme"
                  onSelect={() => run(() => setPreference('light'))}
                />
                <PaletteItem
                  value="theme dark"
                  icon={<Moon />}
                  label="Use dark theme"
                  onSelect={() => run(() => setPreference('dark'))}
                />
                <PaletteItem
                  value="theme system"
                  icon={<Monitor />}
                  label="Use system theme"
                  onSelect={() => run(() => setPreference('system'))}
                />
                {auth.mode === 'dev' &&
                  DEV_ROLES.filter((r) => r !== auth.devRole).map((r) => (
                    <PaletteItem
                      key={r}
                      value={`role ${r}`}
                      icon={<UserCog />}
                      label={`Act as ${r}`}
                      hint="dev"
                      onSelect={() => run(() => auth.setDevRole?.(r))}
                    />
                  ))}
              </Command.Group>
            </Command.List>
          </Command>
        </D.Content>
      </D.Portal>
    </D.Root>
  )
}

function PaletteItem(props: {
  value: string
  label: string
  icon: ReactNode
  hint?: string
  shortcut?: string
  keywords?: string[]
  /** Show regardless of the search text. */
  forceMount?: boolean
  onSelect: () => void
}) {
  return (
    <Command.Item
      value={props.value}
      forceMount={props.forceMount}
      keywords={props.keywords ?? [props.label]}
      onSelect={props.onSelect}
      className="flex h-9 cursor-pointer items-center gap-2.5 rounded-md px-2 text-sm text-ink outline-none select-none data-[selected=true]:bg-surface-3 [&>svg]:size-4 [&>svg]:shrink-0 [&>svg]:text-ink-3"
    >
      {props.icon}
      <span className="truncate">{props.label}</span>
      {props.hint && (
        <span className="ml-auto truncate pl-3 font-mono text-xs text-ink-3">{props.hint}</span>
      )}
      {props.shortcut && (
        <span className="ml-auto flex gap-1 pl-3">
          {props.shortcut.split(' ').map((k) => (
            <Kbd key={k}>{k.toUpperCase()}</Kbd>
          ))}
        </span>
      )}
    </Command.Item>
  )
}
